import { ColumnConfiguration, DefaultFilterOptions, TableFilterModel, TableSortModel } from '@/types';
import { ColumnFilterType } from '@/types/enums/ColumnFilterType';
import { DateUtils } from './dateUtils';
import { BvTableField } from 'bootstrap-vue';

// Filter functions
const filterContains = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return filterKeyValue
    .toString()
    .toLowerCase()
    .includes(filter.value.toLowerCase());
};

const filterEquals = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return filterKeyValue.toString().toLowerCase() === filter.value.toLowerCase();
};

const filterDoesNotEqual = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return filterKeyValue.toString().toLowerCase() !== filter.value.toLowerCase();
};

const filterLessThan = (item: any, filter: TableFilterModel): boolean => {
  const filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue) && DateUtils.isValidDate(filter.value)) {
    const queueItemDate = DateUtils.convertQueueItemValueToDate(filterKeyValue);
    const filterDate = DateUtils.convertFilterValueToDate(filter.value);
    return (queueItemDate ?? 0) < filterDate.getTime();
  }
  return filterKeyValue < filter.value;
};

const filterLessThanOrEqualTo = (item: any, filter: TableFilterModel): boolean => {
  const filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue) && DateUtils.isValidDate(filter.value)) {
    const queueItemDate = DateUtils.convertQueueItemValueToDate(filterKeyValue);
    const filterDate = DateUtils.convertFilterValueToDate(filter.value);
    return (queueItemDate ?? 0) <= filterDate.getTime();
  }
  return filterKeyValue <= filter.value;
};

const filterGreaterThan = (item: any, filter: TableFilterModel): boolean => {
  const filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue) && DateUtils.isValidDate(filter.value)) {
    const queueItemDate = DateUtils.convertQueueItemValueToDate(filterKeyValue);
    const filterDate = DateUtils.convertFilterValueToDate(filter.value);
    return (queueItemDate ?? 0) > filterDate.getTime();
  }
  return filterKeyValue > filter.value;
};

const filterGreaterThanOrEqualTo = (item: any, filter: TableFilterModel): boolean => {
  const filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue) && DateUtils.isValidDate(filter.value)) {
    const queueItemDate = DateUtils.convertQueueItemValueToDate(filterKeyValue);
    const filterDate = DateUtils.convertFilterValueToDate(filter.value);
    return (queueItemDate ?? 0) >= filterDate.getTime();
  }
  return filterKeyValue >= filter.value;
};

const filterStartsWith = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return filterKeyValue
    .toString()
    .toLowerCase()
    .startsWith(filter.value.toLowerCase());
};

const filterEndsWith = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return filterKeyValue
    .toString()
    .toLowerCase()
    .endsWith(filter.value.toLowerCase());
};

const filterDoesNotContain = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return !filterKeyValue
    .toString()
    .toLowerCase()
    .includes(filter.value.toLowerCase());
};

const filterDoesNotStartWith = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return !filterKeyValue
    .toString()
    .toLowerCase()
    .startsWith(filter.value.toLowerCase());
};

const filterDoesNotEndWith = (item: any, filter: TableFilterModel): boolean => {
  let filterKeyValue = item[filter.key] ?? '';
  if (DateUtils.isIsoDate(filterKeyValue)) {
    filterKeyValue = DateUtils.convertIsoDateToString(filterKeyValue);
  }
  return !filterKeyValue
    .toString()
    .toLowerCase()
    .endsWith(filter.value.toLowerCase());
};

export const MStoreTableUtils = {
  applyDefaultSorting<T>(items: T[], sortOptions: TableSortModel[]): T[] {
    if (sortOptions.length === 0) {
      return items;
    }

    const sortedItems = items.sort((a: any, b: any) => {
      for (const sortOption of sortOptions) {
        if (typeof a[sortOption.sortingKey] === 'number' && typeof b[sortOption.sortingKey] === 'number') {
          const sortVal = sortOption.isSortingAscending ? a[sortOption.sortingKey] - b[sortOption.sortingKey] : b[sortOption.sortingKey] - a[sortOption.sortingKey];

          if (sortVal !== 0) {
            return sortVal;
          }
        } else if (typeof a[sortOption.sortingKey] === 'string' && typeof b[sortOption.sortingKey] === 'string') {
          const sortVal = sortOption.isSortingAscending ? a[sortOption.sortingKey].localeCompare(b[sortOption.sortingKey]) : b[sortOption.sortingKey].localeCompare(a[sortOption.sortingKey]);

          if (sortVal !== 0) {
            return sortVal;
          }
        } else if (a[sortOption.sortingKey] == null && b[sortOption.sortingKey] != null) {
          return sortOption.isSortingAscending ? 1 : -1;
        } else if (b[sortOption.sortingKey] == null && a[sortOption.sortingKey] != null) {
          return sortOption.isSortingAscending ? -1 : 1;
        }
      }
      return 0;
    });

    return [...sortedItems];
  },
  applyDefaultFiltering<T>(items: T[], filterOptions: TableFilterModel[]): T[] {
    if (!filterOptions.every((filter) => filter.key && filter.value && filter.columnFilterType)) {
      return items;
    }

    const anyFieldFilterIndex = filterOptions.findIndex((x) => x.key === DefaultFilterOptions.Any.key);
    const anyFieldFilter = anyFieldFilterIndex >= 0 ? filterOptions.splice(anyFieldFilterIndex, 1)[0] : null;

    let filteredItems = [...items];

    // Do filtering based on filter fields
    filterOptions.forEach((filter: TableFilterModel) => {
      if (filter.value) {
        switch (filter.columnFilterType) {
          case ColumnFilterType.Contains:
          case ColumnFilterType.Like:
            filteredItems = filteredItems.filter((x: any) => filterContains(x, filter));
            break;
          case ColumnFilterType.Equals:
            filteredItems = filteredItems.filter((x: any) => filterEquals(x, filter));
            break;
          case ColumnFilterType.DoesNotEqual:
            filteredItems = filteredItems.filter((x: any) => filterDoesNotEqual(x, filter));
            break;
          case ColumnFilterType.LessThan:
            filteredItems = filteredItems.filter((x: any) => filterLessThan(x, filter));
            break;
          case ColumnFilterType.LessThanOrEqualTo:
            filteredItems = filteredItems.filter((x: any) => filterLessThanOrEqualTo(x, filter));
            break;
          case ColumnFilterType.GreaterThan:
            filteredItems = filteredItems.filter((x: any) => filterGreaterThan(x, filter));
            break;
          case ColumnFilterType.GreaterThanOrEqualTo:
            filteredItems = filteredItems.filter((x: any) => filterGreaterThanOrEqualTo(x, filter));
            break;
          case ColumnFilterType.StartsWith:
            filteredItems = filteredItems.filter((x: any) => filterStartsWith(x, filter));
            break;
          case ColumnFilterType.EndsWith:
            filteredItems = filteredItems.filter((x: any) => filterEndsWith(x, filter));
            break;
          case ColumnFilterType.DoesNotContain:
          case ColumnFilterType.NotLike:
            filteredItems = filteredItems.filter((x: any) => filterDoesNotContain(x, filter));
            break;
          case ColumnFilterType.DoesNotStartWith:
            filteredItems = filteredItems.filter((x: any) => filterDoesNotStartWith(x, filter));
            break;
          case ColumnFilterType.DoesNotEndWith:
            filteredItems = filteredItems.filter((x: any) => filterDoesNotEndWith(x, filter));
            break;
        }
      }
    });

    if (anyFieldFilter?.value) {
      switch (anyFieldFilter.columnFilterType) {
        case ColumnFilterType.Contains:
        case ColumnFilterType.Like:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterContains(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.Equals:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterEquals(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.DoesNotEqual:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterDoesNotEqual(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.LessThan:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterLessThan(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.LessThanOrEqualTo:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterLessThanOrEqualTo(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.GreaterThan:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterGreaterThan(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.GreaterThanOrEqualTo:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterGreaterThanOrEqualTo(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.StartsWith:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterStartsWith(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.EndsWith:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterEndsWith(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.DoesNotContain:
        case ColumnFilterType.NotLike:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterDoesNotContain(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.DoesNotStartWith:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterDoesNotStartWith(item, { ...anyFieldFilter, key })));
          break;
        case ColumnFilterType.DoesNotEndWith:
          filteredItems = filteredItems.filter((item: any) => Object.keys(item).some((key) => filterDoesNotEndWith(item, { ...anyFieldFilter, key })));
          break;
      }
    }

    return filteredItems;
  },
  applyDefaultColumnConfigurations(columnDefinitions: Array<{ key: string } & BvTableField>, columnConfigurations: ColumnConfiguration[]): Array<{ key: string } & BvTableField> {
    if (!columnDefinitions) {
      return [];
    }

    if (!columnConfigurations.length) {
      return [...columnDefinitions];
    }
    const orderedColumns = columnConfigurations
      .map((columnConfiguration) => {
        const columnDefinition = columnDefinitions.find((column) => column.key === columnConfiguration.key);
        if (!columnDefinition) {
          return columnDefinition;
        }
        const configuredColumnDefinition = { ...columnDefinition };
        if (!columnConfiguration.visible) {
          configuredColumnDefinition.class += ' d-none';
        }
        return { ...configuredColumnDefinition };
      })
      .filter((x) => x !== undefined) as Array<{ key: string } & BvTableField>;
    const unorderedColumns = columnDefinitions.filter((column) => columnConfigurations.every((columnConfiguration: ColumnConfiguration) => column.key !== columnConfiguration.key));

    return [...orderedColumns, ...unorderedColumns];
  }
};
